import {
  createTableHighlightsRenderingStrategy,
  DomTableGeometryResolver,
  type GeometryResolver,
  HighlightsController,
  TABLE_HIGHLIGHTS_RESIZE_OBSERVE_ELEMENT_ID,
  VirtualGeometryResolver,
} from '@databases/highlights';
import '@databases/highlights/styles';
import {
  FIXED_HIGHLIGHTS_SURFACE_Z_INDEX,
  getTableBaseFragmentZIndex,
} from '@databases/selections';
import { container } from '@databases/service-container';
import { createTimelineHighlightsController as createTimelineLayoutHighlightsController } from '@databases/timeline-layout/timeline-highlights-controller';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { getTableV2GeometryTarget } from '@databases/view-layout-table-v2/dom';
import { createTableV2HighlightsStrategy } from '@databases/view-layout-table-v2/highlights';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { createTableHighlightsLayoutAdapter } from './create-table-highlights-layout-adapter.ts';
import { type CurrentViewIdentity } from './get-current-view-layout-type.ts';
import { TableV2GeometryResolver } from './table-v2-geometry-resolver.ts';
import { type Recyclable } from './types.ts';

const SCROLLABLE_INNER_CONTAINER_ID = 'scrollable-inner-container';
const SELECTIONS_CONTAINER_ELEMENT_ID = 'selections-overlay-container';

type HighlightsControllerFactory = () => HighlightsController;

const INACTIVE_GEOMETRY_RESOLVER: GeometryResolver = {
  getScrollContainer: () => null,
  getCellBounds: () => null,
  getRowBounds: () => null,
  getColumnBounds: () => null,
  getContainerBounds: () => null,
};

function createTableHighlightsController(): HighlightsController {
  const baseResolver = new DomTableGeometryResolver(() =>
    document.getElementById(SCROLLABLE_INNER_CONTAINER_ID),
  );
  const resolver = new VirtualGeometryResolver(baseResolver);
  const layout = createTableHighlightsLayoutAdapter(() => resolver.getScrollContainer());
  // https://switcheroo.atlassian.com/ui/gates/03224aff-4ea6-4b69-b340-24b388b98dd8/key/databases-frontend-highlight-scroll-reliability
  const enableNullBoundsRetry = getExperimentValue(
    'databasesFrontendHighlightScrollReliability',
    'enabled',
    { fireExperimentExposure: false },
  );

  return new HighlightsController(resolver, {
    id: SELECTIONS_CONTAINER_ELEMENT_ID,
    invalidateGeometry: () => resolver.clearCache(),
    getResizeObserveTarget: () =>
      document.getElementById(TABLE_HIGHLIGHTS_RESIZE_OBSERVE_ELEMENT_ID),
    createRenderingStrategy: (context) =>
      createTableHighlightsRenderingStrategy({
        ...context,
        layout,
        pinnedLeadingSurfaceZIndex: FIXED_HIGHLIGHTS_SURFACE_Z_INDEX,
        getBaseFragmentZIndex: getTableBaseFragmentZIndex,
        enableNullBoundsRetry,
      }),
  });
}

/**
 * V2 projects highlights into renderer-owned sticky and scrolling regions.
 * Lazy renderer attachment explicitly mounts/redraws this controller.
 */
function createTableV2HighlightsController(): HighlightsController {
  return new HighlightsController(
    new TableV2GeometryResolver(() =>
      document.getElementById(SCROLLABLE_INNER_CONTAINER_ID),
    ),
    {
      id: SELECTIONS_CONTAINER_ELEMENT_ID,
      createRenderingStrategy: () =>
        createTableV2HighlightsStrategy(() => {
          const owner = document.getElementById(SCROLLABLE_INNER_CONTAINER_ID);
          return owner ? getTableV2GeometryTarget(owner) : undefined;
        }),
    },
  );
}

function createInactiveHighlightsController(): HighlightsController {
  return new HighlightsController(INACTIVE_GEOMETRY_RESOLVER, {
    id: SELECTIONS_CONTAINER_ELEMENT_ID,
  });
}

function createTimelineHighlightsController(): HighlightsController {
  return createTimelineLayoutHighlightsController({
    id: SELECTIONS_CONTAINER_ELEMENT_ID,
  });
}

const HIGHLIGHTS_CONTROLLER_FACTORIES: Readonly<
  Record<string, HighlightsControllerFactory | undefined>
> = {
  [DatabaseLayoutType.TABLE]: createTableHighlightsController,
  [TABLE_V2_LAYOUT_ID]: createTableV2HighlightsController,
  [DatabaseLayoutType.TIMELINE]: createTimelineHighlightsController,
};

/**
 * Registers the layout-scoped highlight composition root.
 *
 * Each layout uses one immutable controller selected by its factory. The
 * recyclable coordinator replaces it before dependent features rebuild.
 */
export function initializeTableHighlightsController(
  readViewIdentity: (contentId: string) => CurrentViewIdentity,
): Recyclable {
  let activeController: HighlightsController | undefined;

  function setup(contentId: string) {
    activeController?.dispose();

    const layoutType = readViewIdentity(contentId).layoutType;
    const createController =
      HIGHLIGHTS_CONTROLLER_FACTORIES[layoutType] ?? createInactiveHighlightsController;
    const controller = createController();

    activeController = controller;
    container.registerSingleton('TableHighlightsController', () => controller);
  }

  function dispose() {
    activeController?.dispose();
    activeController = undefined;
    container.unregister('TableHighlightsController');
  }

  return {
    recycleOn: ['layout-type-changed'],
    setup,
    dispose,
  };
}
